import { useSyncExternalStore } from 'react';

/**
 * The «access epoch» (decision 151, #550): one counter that every read of the caller's access
 * depends on — permissions per module, scopes, the eligible organizations and so the rail. When it
 * moves, those reads run again, without a reload.
 *
 * It moves when the server says the caller's access changed (`useAccessVersionPoller`), and at once
 * after an administration write made in this tab (a module activated, a role granted or revoked),
 * since that write is the likeliest change of all and the poll would show it up to 60 s late.
 *
 * A re-read caused by the epoch keeps the previous answer on screen until the new one lands, so the
 * rail does not blink empty each time; only a switch of organization shows «loading».
 */
let epoch = 0;
const listeners = new Set<() => void>();

export function bumpAccessEpoch(): void {
  epoch += 1;
  for (const listener of listeners) {
    listener();
  }
}

let pending: ReturnType<typeof setTimeout> | null = null;

/**
 * After an administration write: one bump per burst. A bulk grant writes once per person and role;
 * the reads should run once, after the last of them.
 */
export function scheduleAccessEpochBump(delayMs = 300): void {
  if (pending !== null) {
    clearTimeout(pending);
  }
  pending = setTimeout(() => {
    pending = null;
    bumpAccessEpoch();
  }, delayMs);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function snapshot(): number {
  return epoch;
}

export function useAccessEpoch(): number {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}
